import { Alert, Button, Link, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'preact/hooks'
import { ApiError, api } from '../api/client'
import type { ChallengeCreated } from '../api/generated/ChallengeCreated'
import type { CodeLogin } from '../api/generated/CodeLogin'
import type { EmailLoginRequest } from '../api/generated/EmailLoginRequest'
import type { Me } from '../api/generated/Me'
import type { SmsLoginRequest } from '../api/generated/SmsLoginRequest'
import { useAction } from '../api/useAction'
import { formatPhone } from '../phone/format'
import { PhoneField } from '../phone/PhoneField'
import { useSession } from './session'

const contacts = {
  email: {
    label: 'Email',
    newLabel: 'Nouvel email',
    field: { type: 'email' as const, autoComplete: 'email' },
    request: (email: string) =>
      api.post<ChallengeCreated>('/me/email', { email } satisfies EmailLoginRequest),
    sent: 'Code envoyé par email à',
  },
  phone: {
    label: 'Téléphone mobile',
    newLabel: 'Nouveau numéro',
    field: { type: 'tel' as const, autoComplete: 'tel' },
    request: (phone: string) =>
      api.post<ChallengeCreated>('/me/phone', { phone } satisfies SmsLoginRequest),
    sent: 'Code envoyé par SMS au',
  },
}

const contactErrors = {
  reauthentication_required:
    'Par sécurité, reconnectez-vous avant de modifier votre email ou votre téléphone.',
}

/** A contact point of the account, replaced once a code sent to the new one is typed. */
export function ContactForm({ kind }: { kind: keyof typeof contacts }) {
  const { me, setMe, logout } = useSession()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [code, setCode] = useState('')
  const [challengeId, setChallengeId] = useState<string>()
  const [mustLogIn, setMustLogIn] = useState(false)
  const { error, pending, run } = useAction(contactErrors)
  const contact = contacts[kind]
  const current = me?.[kind]

  const reset = () => {
    setEditing(false)
    setValue('')
    setChallengeId(undefined)
  }

  const requestCode = (event: Event) => {
    event.preventDefault()
    run(async () => {
      try {
        const { challenge_id } = await contact.request(value)
        setChallengeId(challenge_id)
        setCode('')
      } catch (e) {
        setMustLogIn(e instanceof ApiError && e.code === 'reauthentication_required')
        throw e
      }
    })
  }

  const submitCode = (event: Event) => {
    event.preventDefault()
    if (!challengeId) return
    run(async () => {
      const body: CodeLogin = { challenge_id: challengeId, code }
      setMe(await api.post<Me>('/me/contact', body))
      reset()
    })
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">{contact.label}</Typography>
      {!editing && (
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Typography sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {current ? kind === 'phone' ? formatPhone(current) : current : <em>Aucun</em>}
          </Typography>
          <Button onClick={() => setEditing(true)}>{current ? 'Modifier' : 'Ajouter'}</Button>
        </Stack>
      )}
      {editing && challengeId === undefined && (
        <Stack component="form" spacing={2} onSubmit={requestCode}>
          {kind === 'phone' ? (
            <PhoneField
              label={current ? contact.newLabel : contact.label}
              autoComplete={contact.field.autoComplete}
              required
              autoFocus
              value={value}
              onValue={setValue}
            />
          ) : (
            <TextField
              key={kind}
              {...contact.field}
              label={current ? contact.newLabel : contact.label}
              required
              autoFocus
              value={value}
              onInput={(e) => setValue((e.target as HTMLInputElement).value)}
            />
          )}
          {error && (
            <Alert
              severity="error"
              action={
                mustLogIn && (
                  <Button color="inherit" size="small" onClick={logout}>
                    Se reconnecter
                  </Button>
                )
              }
            >
              {error}
            </Alert>
          )}
          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="contained" loading={pending}>
              Recevoir un code
            </Button>
            <Button onClick={reset}>Annuler</Button>
          </Stack>
        </Stack>
      )}
      {editing && challengeId !== undefined && (
        <Stack component="form" spacing={2} onSubmit={submitCode}>
          <Typography>
            {contact.sent} <strong>{value}</strong>.
          </Typography>
          <TextField
            label="Code à 6 chiffres"
            required
            autoFocus
            autoComplete="one-time-code"
            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]{6}', maxLength: 6 } }}
            value={code}
            onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="contained" loading={pending}>
              Valider
            </Button>
            <Button onClick={reset}>Annuler</Button>
          </Stack>
          <Link component="button" type="button" onClick={() => setChallengeId(undefined)}>
            Corriger ou renvoyer un code
          </Link>
        </Stack>
      )}
    </Stack>
  )
}

/** The user's name and contact points, which organizers see in their events, and passkeys. */
